// The live delivery tracker's rules: which of the guest's orders is a delivery
// still worth showing, how far along it is, and what to call each step.
//
// A port of the app's hooks/delivery/status.ts (hey_waitr), kept pure so the
// floating button and its panel (components/DeliveryTracker) can never disagree
// about a delivery, and so every rule is testable without a network.
//
// Inputs are what the backend serialises: GET /orders/fetch/recent with
// includeLiveDelivery=true (woltFieldsFor, restaurantPhone) and
// GET /orders/checkstatus. The backend only ever moves a Wolt status forward
// and never out of a failure (woltWebhook.js STATUS_RANK / TERMINAL_FAIL).
// The menu page also hands a delivery over the moment the venue accepts it —
// see the end of this file.

// Highest step on the happy path: 0 is "preparing", 5 is "delivered".
export const FINAL_STAGE_INDEX = 5;

// A delivery that ARRIVED needs nothing more; the button goes.
export const TERMINAL_SUCCESS = new Set(['delivered', 'dropoff_completed']);

// A delivery that FAILED is when the guest most needs something to act on, so
// it stays — with the failure and the venue's number — until they dismiss it.
export const TERMINAL_FAIL = new Set(['rejected', 'cancelled', 'customer_no_show']);

export const STAGE_LABEL_KEYS = [
  'delivery_status_preparing',
  'delivery_status_courier_booked',
  'delivery_status_courier_to_venue',
  'delivery_status_on_the_way',
  'delivery_status_arrived',
  'delivery_status_delivered',
];

// Several Wolt statuses share a step on purpose: starting the trip to the
// restaurant and arriving there are one idea to a guest.
const STATUS_STAGE = {
  received: 1,
  pickup_started: 2,
  pickup_arrival: 2,
  picked_up: 3,
  dropoff_started: 3,
  dropoff_arrival: 4,
  dropoff_completed: FINAL_STAGE_INDEX,
  delivered: FINAL_STAGE_INDEX,
};

const FAILURE_OUTCOME = { rejected: 'failed', customer_no_show: 'failed', cancelled: 'cancelled' };
const FAILURE_LABEL_KEYS = { failed: 'delivery_status_failed', cancelled: 'delivery_status_cancelled' };

const stageOf = (index, outcome) => ({
  index,
  outcome,
  labelKey: STAGE_LABEL_KEYS[index],
  isTerminal: outcome === 'delivered',
});

const failureStage = (outcome) => ({
  index: null,
  outcome,
  labelKey: FAILURE_LABEL_KEYS[outcome],
  isTerminal: true,
});

const clampStage = (index) => {
  if (!Number.isFinite(index) || index < 0) return 0;
  if (index > FINAL_STAGE_INDEX) return FINAL_STAGE_INDEX;
  return Math.floor(index);
};

/**
 * The step a stored delivery status is shown as. The venue's "Annuller Wolt"
 * (cancelledAt) outranks whatever the courier last reported. A status this code
 * does not know holds the step last shown rather than snapping back.
 */
export const deliveryStage = (status, cancelledAt, previousIndex = 0) => {
  if (cancelledAt) return failureStage('cancelled');
  if (!status) return stageOf(0, 'progress');

  const failure = FAILURE_OUTCOME[status];
  if (failure) return failureStage(failure);

  const index = STATUS_STAGE[status];
  if (index === undefined) {
    const held = clampStage(previousIndex);
    return stageOf(held, held === FINAL_STAGE_INDEX ? 'delivered' : 'progress');
  }
  return stageOf(index, index === FINAL_STAGE_INDEX ? 'delivered' : 'progress');
};

// Ring fill, 0–100. A failure reads as an empty ring.
export const stageFill = (stage) =>
  stage.index === null ? 0 : Math.round((stage.index / FINAL_STAGE_INDEX) * 100);

// A running delivery the tracker gives up on this long after it was due —
// guards against an order stuck in dispatch_unknown pinning the button forever.
export const ACTIVE_DELIVERY_TTL_HOURS = 6;
// A failure is retired only by the guest; this is just the backstop. Matches
// the backend's failed-delivery window in /orders/fetch/recent.
export const FAILED_DELIVERY_TTL_HOURS = 7 * 24;

// A device clock running behind the server's must not hide a delivery the
// venue accepted moments after it was placed — an auto-accept venue does so
// within one poll — so a placement up to this far "ahead" counts as just made.
const CLOCK_SKEW_MS = 10 * 60 * 1000;

const timeOf = (iso) => {
  if (!iso) return null;
  const at = new Date(iso).getTime();
  return Number.isNaN(at) ? null : at;
};

export const hasDeliveryFailed = (delivery) =>
  Boolean(delivery.cancelledAt) || (Boolean(delivery.status) && TERMINAL_FAIL.has(delivery.status));

/**
 * What the button and the panel say. A failed delivery on a refunded order
 * says "cancelled" and "refunded" outright — "we couldn't complete this
 * delivery" alone leaves the guest unsure whether food or money is coming.
 * (order_refunded_message is the web's delivery-refund sentence.)
 */
export const deliveryPillLabelKey = (delivery, stage) =>
  delivery.refunded && hasDeliveryFailed(delivery) ? 'order_refunded_title' : stage.labelKey;

export const deliveryFailureTextKey = (delivery, stage) =>
  delivery.refunded && hasDeliveryFailed(delivery) ? 'order_refunded_message' : stage.labelKey;

// Order statuses a running delivery may sit behind. 'completed' is here because
// a venue may complete the order at courier handoff, long before the food
// arrives — the courier's status, not the order's, says when tracking ends.
const LIVE_ORDER_STATUS = new Set(['confirmed', 'completed']);

/**
 * Is this delivery still worth showing? Dine-in never is; a dismissed failure
 * never is; a running delivery needs a live order behind it; a failure stays
 * whatever the venue did to the order since (it must reach the guest).
 */
export const isDeliveryActive = (delivery, orderStatus, isDineIn, now = Date.now()) => {
  if (isDineIn || delivery.dismissed) return false;

  const failed = hasDeliveryFailed(delivery);
  if (!failed && !LIVE_ORDER_STATUS.has(orderStatus)) return false;
  if (failed && orderStatus === 'preorder') return false;
  if (delivery.status && TERMINAL_SUCCESS.has(delivery.status)) return false;

  const placed = timeOf(delivery.placedAt);
  if (placed === null || placed - now > CLOCK_SKEW_MS) return false;

  // A failure counts from when it was placed; a running delivery from when it
  // is due. A scheduled one can be placed many hours ahead, and once a courier
  // is booked Wolt's ETA is that time — the only one an order read back after
  // a payment redirect has.
  const due = failed
    ? placed
    : Math.max(
        placed,
        timeOf(delivery.scheduledFor) ?? placed,
        timeOf(delivery.dropoffETA) ?? placed,
      );
  const ttlHours = failed ? FAILED_DELIVERY_TTL_HOURS : ACTIVE_DELIVERY_TTL_HOURS;
  return now - due <= ttlHours * 60 * 60 * 1000;
};

/**
 * Whole minutes until an ETA, or null for none, an unreadable one, or one that
 * has already passed — "in -3 min" is worse than admitting we do not know.
 */
export const minutesUntil = (iso, now = Date.now()) => {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  const remaining = target - now;
  if (remaining <= 0) return null;
  return Math.max(1, Math.ceil(remaining / 60000));
};

// The best tracking link on offer: the backend's short link first.
export const trackingLinkOf = (data) =>
  data?.woltTrackingLink ||
  data?.woltDeliveryTrackingUrl ||
  data?.woltDeliveryAccept?.tracking?.url ||
  null;

/**
 * One order from /orders/fetch/recent as a tracked delivery, or null when it is
 * not a Wolt delivery. `woltDelivery` (the stored promise, or false) is the only
 * delivery marker the list sends.
 */
export const toActiveDelivery = (order) => {
  if (!order?.woltDelivery || !order?._id) return null;
  return {
    orderId: String(order._id),
    refNo: String(order.refNo ?? ''),
    restaurantId: String(order.restaurantId ?? ''),
    restaurantName: String(order.restaurant ?? ''),
    restaurantPhone: order.restaurantPhone || null,
    status: order.woltDeliveryStatus || null,
    dropoffETA: order.woltDeliveryDropoffETA || null,
    pickupETA: order.woltDeliveryPickupETA || null,
    dropoffAddress: order.woltDropoffAddress || null,
    trackingLink: trackingLinkOf(order),
    cancelledAt: order.woltDeliveryCancelledAt || null,
    placedAt: order.date,
    scheduledFor: (order.isAdvanceOrder && order.advanceOrderDate) || null,
    refunded: order.status === 'refunded',
  };
};

/**
 * The recent-orders payload down to the deliveries still worth showing, newest
 * first. Dismissals are re-applied every time: the server never learns of them.
 */
export const projectActiveDeliveries = (orders, now = Date.now(), dismissedIds = new Set()) => {
  if (!Array.isArray(orders)) return [];
  return orders
    .map((order) => {
      const projected = toActiveDelivery(order);
      if (!projected) return null;
      const delivery = { ...projected, dismissed: dismissedIds.has(projected.orderId) };
      const active = isDeliveryActive(
        delivery,
        String(order.status ?? ''),
        order.isDineIn === true,
        now,
      );
      return active ? delivery : null;
    })
    .filter(Boolean)
    .sort((a, b) => new Date(b.placedAt).getTime() - new Date(a.placedAt).getTime());
};

// /orders/checkstatus answers with a `type`, not the order's Status.
const ORDER_STATUS_BY_TYPE = {
  order_accepted: 'confirmed',
  order_completed: 'completed',
  order_cancelled: 'cancelled',
  order_refunded: 'refunded',
  order_forpayment: 'forpayment',
};

/**
 * A tracked delivery updated from /orders/checkstatus. Only the fields that
 * endpoint carries move; venue name, phone and address come from the list and
 * survive. The short tracking link is never overwritten by the raw Wolt URL,
 * and a refund, once seen, is never undone. An unknown `type` counts as still
 * confirmed rather than blanking a live delivery. A delivery with no address
 * yet takes the one Wolt formatted when it booked the courier.
 */
export const mergeDeliveryStatus = (existing, data) => ({
  delivery: {
    ...existing,
    status: data.woltDeliveryStatus ?? existing.status,
    dropoffETA: data.woltDeliveryDropoffETA ?? existing.dropoffETA,
    pickupETA: data.woltDeliveryPickupETA ?? existing.pickupETA,
    cancelledAt: data.woltDeliveryCancelledAt ?? existing.cancelledAt,
    trackingLink: existing.trackingLink ?? trackingLinkOf(data),
    dropoffAddress:
      existing.dropoffAddress ??
      data.woltDeliveryAccept?.dropoff?.location?.formatted_address ??
      null,
    refunded: existing.refunded || data.type === 'order_refunded',
  },
  orderStatus: ORDER_STATUS_BY_TYPE[data.type] ?? 'confirmed',
});

export const sameDelivery = (a, b) =>
  a.status === b.status &&
  a.dropoffETA === b.dropoffETA &&
  a.pickupETA === b.pickupETA &&
  a.cancelledAt === b.cancelledAt &&
  Boolean(a.refunded) === Boolean(b.refunded) &&
  a.trackingLink === b.trackingLink &&
  a.dropoffAddress === b.dropoffAddress;

// "Something about an order's delivery changed" — the order poller and the
// push handler announce it, the tracker listens. A tracked order is refreshed
// on its own; anything else re-reads the list (the only path that adds one) —
// unless `trackedOnly`: news that cannot start a delivery (a completion, say)
// is no reason to re-read the list for the many orders that are not one.
export const DELIVERY_CHANGED_EVENT = 'hw:delivery-changed';

export const announceDeliveryChange = (orderId, { trackedOnly = false } = {}) => {
  try {
    window.dispatchEvent(
      new CustomEvent(DELIVERY_CHANGED_EVENT, { detail: { orderId, trackedOnly } }),
    );
  } catch (e) {
    // no window (tests of code that does not care) — nothing to tell
  }
};

// Failures the guest has acknowledged, kept across reloads. Capped: the ids
// only matter for the 7 days a failure can stay listed.
const DISMISSED_KEY = 'hw_dismissed_deliveries';
const DISMISSED_CAP = 50;

export const readDismissedDeliveries = () => {
  try {
    const ids = JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]');
    return Array.isArray(ids) ? ids.map(String) : [];
  } catch (e) {
    return [];
  }
};

export const saveDismissedDeliveries = (ids) => {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(ids.slice(-DISMISSED_CAP)));
  } catch (e) {
    // storage full or blocked: the dismissal still holds for this session
  }
};

// ── The menu page's hand-over ─────────────────────────────────────────────
//
// ProcessingOrderModal covers a delivery's wait for the venue — where the
// guest can still cancel — and steps aside for this tracker the moment the
// venue accepts. What is handed over is kept in this browser, so the tracker
// finds it again after a reload whatever the account's list can say of it —
// and a guest who is not signed in is followed at all: /orders/fetch/recent
// needs a login, /orders/checkstatus does not.

/**
 * The order the menu page has just placed, marked when it is a delivery. The
 * placement answer does not say so, and the order poller has to know the
 * moment the venue accepts, before Wolt has booked anything. The address the
 * guest typed stands in until Wolt's own is known; whether they were signed in
 * keeps the order from being handed over after they no longer are.
 */
export const markDeliveryOrder = (order, orderType, address, signedIn) =>
  order && orderType === 'delivery'
    ? {
        ...order,
        isDelivery: true,
        placedSignedIn: Boolean(signedIn),
        dropoffAddress:
          [address?.street, address?.zipcode, address?.city].filter(Boolean).join(', ') || null,
      }
    : order;

/**
 * Is this order a delivery? The menu page's mark, the Wolt promise an order
 * read back from /order/details carries (after a payment redirect), or a
 * courier booked at checkout.
 */
export const isDeliveryOrder = (order) =>
  Boolean(order?.isDelivery || order?.woltDelivery || order?.woltDeliveryAccept);

/**
 * What the tracker needs to follow a handed-over delivery, from the order as
 * the page holds it — placed, or read back from /order/details. Neither says
 * the venue's number, nor does anything a guest who is not signed in can ask;
 * a signed-in guest's comes with their account's list. One handed over signed
 * in is forgotten at sign-out.
 */
export const deliverySeedOf = (order, { signedIn = false } = {}) => ({
  orderId: String(order._id),
  refNo: String(order.refNo ?? ''),
  restaurantId: String(order.restaurantId ?? ''),
  restaurantName: typeof order.restaurant === 'string' ? order.restaurant : '',
  restaurantPhone: null,
  dropoffAddress: order.woltDropoffAddress || order.dropoffAddress || null,
  trackingLink: trackingLinkOf(order),
  placedAt: order.date || new Date().toISOString(),
  scheduledFor: (order.isAdvanceOrder && order.advanceOrderDate) || null,
  signedIn: Boolean(signedIn),
});

// A handed-over delivery before its first status: no step, no ETA yet.
export const deliveryFromSeed = (seed) => ({
  orderId: seed.orderId,
  refNo: seed.refNo || '',
  restaurantId: seed.restaurantId || '',
  restaurantName: seed.restaurantName || '',
  restaurantPhone: seed.restaurantPhone || null,
  status: null,
  dropoffETA: null,
  pickupETA: null,
  dropoffAddress: seed.dropoffAddress || null,
  trackingLink: seed.trackingLink || null,
  cancelledAt: null,
  placedAt: seed.placedAt,
  scheduledFor: seed.scheduledFor || null,
  refunded: false,
});

/**
 * Would the tracker show this delivery, given the status that accepted it? If
 * not — a device clock far behind the server's, say — ProcessingOrderModal
 * stays rather than making way for nothing.
 */
export const handOverShows = (seed, status) => {
  const { delivery, orderStatus } = mergeDeliveryStatus(deliveryFromSeed(seed), status || {});
  return isDeliveryActive(delivery, orderStatus, false);
};

// The deliveries handed over in this browser, kept across reloads.
const OWN_KEY = 'hw_tracked_deliveries';
const OWN_CAP = 10;

const saveOwnDeliveries = (seeds) => {
  try {
    localStorage.setItem(OWN_KEY, JSON.stringify(seeds.slice(-OWN_CAP)));
  } catch (e) {
    // storage full or blocked: followed for this session only
  }
};

// Past the failure backstop no delivery can show again, whatever became of it.
export const readOwnDeliveries = (now = Date.now()) => {
  try {
    const seeds = JSON.parse(localStorage.getItem(OWN_KEY) || '[]');
    if (!Array.isArray(seeds)) return [];
    return seeds.filter((seed) => {
      const placed = timeOf(seed?.placedAt);
      return (
        Boolean(seed?.orderId) &&
        placed !== null &&
        now - placed <= FAILED_DELIVERY_TTL_HOURS * 60 * 60 * 1000
      );
    });
  } catch (e) {
    return [];
  }
};

// Handed over again: what is known is kept.
export const rememberOwnDelivery = (seed) => {
  const seeds = readOwnDeliveries();
  const known = seeds.find((s) => s.orderId === seed.orderId) || {};
  const merged = { ...known };
  Object.keys(seed).forEach((key) => {
    if (seed[key] != null && seed[key] !== '') merged[key] = seed[key];
  });
  saveOwnDeliveries([...seeds.filter((s) => s.orderId !== seed.orderId), merged]);
};

export const forgetOwnDelivery = (orderId) => {
  const seeds = readOwnDeliveries();
  const kept = seeds.filter((s) => s.orderId !== orderId);
  if (kept.length !== seeds.length) saveOwnDeliveries(kept);
};

// Signed out: nothing handed over while signed in stays behind.
export const forgetSignedInDeliveries = () => {
  const seeds = readOwnDeliveries();
  const kept = seeds.filter((s) => !s.signedIn);
  if (kept.length !== seeds.length) saveOwnDeliveries(kept);
};

// Does the tracker follow this order? Then a reload leaves it to the tracker.
export const isFollowedDelivery = (orderId) =>
  readOwnDeliveries().some((seed) => seed.orderId === String(orderId));

export const DELIVERY_HANDOFF_EVENT = 'hw:delivery-handoff';

/**
 * Hand a delivery over to the tracker: kept in this browser, then announced
 * with the /orders/checkstatus answer that accepted it. `open` opens the
 * tracker on it — ProcessingOrderModal was on screen and makes way.
 */
export const handOverDelivery = (seed, status, { open = false } = {}) => {
  rememberOwnDelivery(seed);
  try {
    window.dispatchEvent(
      new CustomEvent(DELIVERY_HANDOFF_EVENT, { detail: { seed, status: status || null, open } }),
    );
  } catch (e) {
    // no window — nothing to announce it to
  }
};
